import React, { useEffect, useMemo } from 'react';
import { faSquarePlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { ToolButton } from '../../../../components';
import { alertTopic, hideAlert } from '../../../../components/alert/Alert';
import { AlertTypeEnum, StateTopicEnum } from '../../../../enums';
import { IToolButtonProps } from '../../../../interfaces';
import { userDataService } from '../../services';
import { UserPermissions } from '../../../../../src/definitions';
import { securityService } from '../../../../../src/_global/services';
import { publish, useStateReducer } from '../../../../hooks';
import TextField from '@mui/material/TextField';
import { Grid } from '@mui/material';
import './_style.add-button-users.scss';
import * as globalConfig from '../../../../_config/global';
import {
  emailRegex,
  isNameValidRegex,
  isNumberRegex,
  isSpecialCharRegex
} from '../../../../definitions/Regexes';

const checkValidEmail = (email: string) => {
  return emailRegex.test(email);
};

const validName = (name: string) => {
  return (
    isNameValidRegex.test(name) &&
    !isSpecialCharRegex.test(name) &&
    !isNumberRegex.test(name) &&
    /[A-Za-z]/.test(name) &&
    !/^-|-$/.test(name)
  );
};

export default function AddButton({ config }: IToolButtonProps) {
  const navigate = useNavigate();
  const { id: moduleId } = config;

  const [state, setState] = useStateReducer({
    email: '',
    isOpened: false,
    firstName: '',
    lastName: '',
    emailError: '',
    password: globalConfig.default.defaults.default_password,
    emailVerified: true
  });

  const validEmail = checkValidEmail(state.email);
  const isValidFirstName = validName(state.firstName);
  const isValidLastName = validName(state.lastName);

  const showContent = () => {
    let actions;

    if (validEmail && isValidFirstName && isValidLastName) {
      actions = [
        { text: 'No', onClick: hideAlert },

        { text: 'Yes', onClick: handleYes, primary: true }
      ];
    } else {
      actions = [
        { text: 'No', onClick: hideAlert },

        { text: 'Yes', onClick: handleYes, disabled: true, primary: true }
      ];
    }
    publish(alertTopic, {
      content,

      options: {
        actions: actions,
        type: AlertTypeEnum.Question
      }
    });
  };

  const preventSpace = (event: React.KeyboardEvent) => {
    if (event.key === ' ') {
      event?.preventDefault();
    }
  };

  const content = useMemo(() => {
    return (
      <div className='users-textfields'>
        Are you sure you want to add a new user?
        <Grid container direction={'row'} spacing={3}>
          <Grid item>
            <TextField
              fullWidth
              label='First Name'
              value={state.firstName}
              onChange={(e) => setState({ firstName: e.target.value })}
              error={!isValidFirstName}
              helperText={
                !isValidFirstName &&
                'Name should have no special characters or numbers'
              }
            />
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              label='Last Name'
              value={state.lastName}
              onChange={(e) => setState({ lastName: e.target.value })}
              error={!isValidLastName}
              helperText={
                !isValidLastName &&
                'Name should have no special characters or numbers.'
              }
            />
          </Grid>
        </Grid>
        <TextField
          fullWidth
          type='email'
          label='Email Address'
          value={state.email}
          onChange={(e) => setState({ email: e.target.value })}
          onKeyDown={preventSpace}
          error={!validEmail}
          helperText={!validEmail && 'Email should be a valid email address'}
        />
        <TextField
          fullWidth
          label='Default Password'
          value={state.password}
          disabled={true}
        />
      </div>
    );
  }, [state.email, state.firstName, state.lastName]);

  const handleClick = () => {
    if (!state.isOpened) {
      setState({ isOpened: true });
      publish(StateTopicEnum.Alert, true);
    }
    showContent();
  };

  const handleYes = async () => {
    const data = await userDataService.create({
      email_address: state.email.trim(),
      first_name: state.firstName.trim(),
      last_name: state.lastName.trim(),
      password: state.password,
      emailVerified: state.emailVerified
    });

    if (!data.success) {
      publish(alertTopic, {
        content: data?.message,
        options: {
          actions: [{ text: 'Close', onClick: showContent, primary: true }],
          type: AlertTypeEnum.Error
        }
      });
      return;
    }

    setState({ email: '', isOpened: false, firstName: '', lastName: '' });
    hideAlert();

    const newId = data?.data?._id;

    navigate(`/${moduleId}/${newId}?mode=edit`, { replace: false });
  };

  useEffect(() => {
    if (state.isOpened) handleClick();
  }, [state.isOpened, state.email, state.firstName, state.lastName]);

  return (
    <ToolButton
      text='Add'
      icon={faSquarePlus}
      primary
      raised
      onClick={handleClick}
      invalid={!securityService.checkRoleAccess(UserPermissions.editUsers)}
    />
  );
}
